// Domaine Apprendre : accueil formation, lecteur de cours en page dédiée, bibliothèque.
// Composants réels de vendor/ubm-ui-components (imports granulaires) ; données dans activities-fixtures.ts ;
// progression lue et écrite dans le store partagé de ./activities (aucun réseau, aucun média inventé).
// Adresse : #/learn, #/learn?level=L1&lesson=l2 (lecteur), #/learn?resource=rp1 (ressource). Le lecteur signale
// son état au shell par onReaderChange(active) : la sidebar globale cède la place à la sidebar du cours.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from "react";
import { ArrowLeft, Check, ChevronLeft, ChevronRight, ExternalLink, FileText, LogOut, ListTree, Lock, PlayCircle } from "lucide-react";

import { Input } from "../vendor/ubm-ui-components/src/components/input";
import { Badge, type BadgeTone } from "../vendor/ubm-ui-components/src/components/badge";
import { BottomSheet } from "../vendor/ubm-ui-components/src/components/bottom-sheet";
import { Button, ButtonCore } from "../vendor/ubm-ui-components/src/components/button";
import { Card, CardSurface } from "../vendor/ubm-ui-components/src/components/card";
import { ChipGroup } from "../vendor/ubm-ui-components/src/components/chip-group";
import { EmptyState } from "../vendor/ubm-ui-components/src/components/empty-state";
import { Progress } from "../vendor/ubm-ui-components/src/components/progress";
import { TreeView, type TreeNode } from "../vendor/ubm-ui-components/src/components/tree-view";

import type { PageProps } from "./contracts";
import { MOBILE_QUERY, useMediaQuery } from "./shared";
import { markLesson, setLessonPosition, useActivities } from "./activities";
import {
  LESSONS, LESSON_COVERS, LEVELS, RESOURCES, RESOURCE_COLLECTIONS, STEPS,
  type LearnProgress, type Lesson, type Level, type LevelAccess, type Resource, type ResourceCollection,
} from "./activities-fixtures";
import "./learning.css";

export type LearnExperienceProps = PageProps & { onReaderChange?: (active: boolean) => void };

/** A Foundry adapter may add a real media URL; the demo fixtures carry none, so no player is ever shown for them. */
type WithMedia = { mediaUrl?: string };

// ── Adresse (hash) ───────────────────────────────────────────────────────────

interface LearnLocation { level?: string; lesson?: string; resource?: string }

/** Reads #/learn?level=…&lesson=… ; unknown ids are dropped, a lesson alone resolves its level. Anything else = accueil. */
function readLearnHash(): LearnLocation {
  const raw = window.location.hash.replace(/^#\/?/, "");
  const q = raw.indexOf("?");
  if (q < 0 || raw.slice(0, q) !== "learn") return {};
  const params = new URLSearchParams(raw.slice(q + 1));
  const pick = (key: string) => {
    const value = params.get(key);
    if (!value) return undefined;
    try { return decodeURIComponent(value).trim() || undefined; } catch { return value.trim() || undefined; }
  };
  const resource = pick("resource");
  if (resource && RESOURCES.some(r => r.id === resource)) return { resource };
  const lessonId = pick("lesson");
  const lesson = lessonId && LESSONS[lessonId] ? lessonId : undefined;
  const lessonLevel = lesson ? STEPS[LESSONS[lesson]!.step]?.level : undefined;
  const levelId = pick("level");
  const level = levelId && LEVELS.some(l => l.id === levelId) ? levelId : lessonLevel;
  if (!level) return {};
  // A lesson from another level than the one asked is not shown under it.
  return lesson && lessonLevel === level ? { level, lesson } : { level };
}

const learnHash = (loc: LearnLocation) => {
  const params = new URLSearchParams();
  if (loc.resource) params.set("resource", loc.resource);
  else {
    if (loc.level) params.set("level", loc.level);
    if (loc.lesson) params.set("lesson", loc.lesson);
  }
  const qs = params.toString();
  return `#/learn${qs ? `?${qs}` : ""}`;
};
const sameLocation = (a: LearnLocation, b: LearnLocation) => a.level === b.level && a.lesson === b.lesson && a.resource === b.resource;

/** Local mirror of the hash. In-app moves use pushState (no event), back/forward and typed links come through the events. */
function useLearnLocation() {
  const [loc, setLoc] = useState<LearnLocation>(readLearnHash);
  useEffect(() => {
    const sync = () => { const next = readLearnHash(); setLoc(prev => (sameLocation(prev, next) ? prev : next)); };
    window.addEventListener("hashchange", sync);
    window.addEventListener("popstate", sync);
    return () => { window.removeEventListener("hashchange", sync); window.removeEventListener("popstate", sync); };
  }, []);
  const go = useCallback((next: LearnLocation, mode: "push" | "replace" = "push") => {
    const hash = learnHash(next);
    if (window.location.hash !== hash) window.history[mode === "push" ? "pushState" : "replaceState"](null, "", hash);
    setLoc(prev => (sameLocation(prev, next) ? prev : next));
  }, []);
  return [loc, go] as const;
}

// ── Règles du parcours (vérité des fixtures) ─────────────────────────────────

const stepComplete = (p: LearnProgress, stepId: string) => (STEPS[stepId]?.lessons ?? []).every(id => LESSONS[id]?.soon || p.done[id]);
const stepLocked = (p: LearnProgress, stepId: string) => { const pre = STEPS[stepId]?.prereq; return !!pre && !stepComplete(p, pre); };
const lessonOpenable = (p: LearnProgress, id: string) => { const l = LESSONS[id]; return !!l && !l.soon && !stepLocked(p, l.step); };
const levelLessonIds = (lv: Level) => lv.steps.flatMap(id => STEPS[id]?.lessons ?? []);
const levelCountable = (lv: Level) => levelLessonIds(lv).filter(id => !LESSONS[id]?.soon);
const levelOfLesson = (id: string) => LEVELS.find(lv => lv.id === STEPS[LESSONS[id]?.step ?? ""]?.level);

/** Where a level opens: the current lesson (last followed, not done) if it belongs to it, else the first open lesson not done,
 *  else the last followed, else the first open one. */
function entryLesson(p: LearnProgress, lv: Level): string | undefined {
  const ids = levelLessonIds(lv).filter(id => lessonOpenable(p, id));
  if (ids.includes(p.last) && !p.done[p.last]) return p.last;
  return ids.find(id => !p.done[id]) ?? (ids.includes(p.last) ? p.last : ids[0]);
}

/** Previous / next lesson in the level order, skipping « bientôt »; a locked neighbour is returned flagged, never opened. */
function neighbour(p: LearnProgress, lv: Level, id: string, dir: 1 | -1): { id: string; locked: boolean } | undefined {
  const ids = levelLessonIds(lv);
  for (let i = ids.indexOf(id) + dir; i >= 0 && i < ids.length; i += dir) {
    const l = LESSONS[ids[i]!];
    if (!l || l.soon) continue;
    return { id: l.id, locked: stepLocked(p, l.step) };
  }
  return undefined;
}

const fmtClock = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
const fmtDay = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long" });
const dayLabel = (iso: string) => { const [y, m, d] = iso.split("-").map(Number) as [number, number, number]; return fmtDay.format(new Date(y, m - 1, d)); };
const formatLabel = (f: Lesson["format"]) => (f === "video" ? "Vidéo" : f === "text" ? "Texte" : f === "file" ? "Fiche" : f === "support" ? "Support écrit" : "Outil");
const ACCESS: Record<LevelAccess, { label: string; tone: BadgeTone }> = {
  included: { label: "Inclus", tone: "success" }, not_included: { label: "Non inclus", tone: "warning" }, soon: { label: "Bientôt", tone: "neutral" },
};
/** Existing level covers of this prototype (assets/tc-level-N-0810.png), by level order. */
const levelCover = (lv: Level) => `./assets/tc-da-level${LEVELS.indexOf(lv) + 1}-0810.png`;
const COLLECTION_LABEL: Record<ResourceCollection, string> = { res: "Ressource", replay: "Replay", tool: "Outil" };

function Notice({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
  return <p className="lrn-notice" role="note">{icon}<span>{children}</span></p>;
}

// ── Contenus partagés (leçon et ressource) ───────────────────────────────────

/** Video block: a real player only when a media URL exists; otherwise an honest unavailable state with the saved position. */
function VideoBlock({ id, durSec, pos, done, mediaUrl, trackPosition }: { id: string; durSec?: number; pos?: number; done?: boolean; mediaUrl?: string; trackPosition: boolean }) {
  const lastSaved = useRef(pos ?? 0);
  if (mediaUrl) {
    return (
      <video className="lrn-video" src={mediaUrl} controls preload="metadata"
        onLoadedMetadata={e => { const video=e.currentTarget; const duration=Number.isFinite(video.duration)?video.duration:50; const saved=Math.min(pos??0,Math.max(0,duration-0.1));video.currentTime=saved;lastSaved.current=Math.floor(saved);if(trackPosition)setLessonPosition(id,Math.floor(saved)); }}
        onTimeUpdate={e => {
          if (!trackPosition) return;
          const t = Math.floor(e.currentTarget.currentTime);
          if (Math.abs(t - lastSaved.current) >= 5) { lastSaved.current = t; setLessonPosition(id, t); }
        }} />
    );
  }
  return (
    <div className="lrn-media-off">
      <Notice icon={<PlayCircle size={18} aria-hidden />}>Vidéo indisponible</Notice>
      {durSec !== undefined && pos !== undefined && pos > 0 && !done && (
        <Progress value={pos} max={durSec} label={`Ta reprise est enregistrée à ${fmtClock(pos)} sur ${fmtClock(durSec)}`} />
      )}
    </div>
  );
}

/** Real metadata only: bracketed placeholders (« [taille] », « [Nom du coach] ») are dropped with their separator. */
const realMeta = (meta?: string) => meta?.replace(/\s*·?\s*\[[^\]]*\]\s*·?/g, " · ").replace(/^\s*·\s*|\s*·\s*$/g, "").replace(/\s{2,}/g, " ").trim() || undefined;

function Files({ files }: { files: NonNullable<Lesson["files"]> }) {
  return (
    <section className="lrn-files" aria-label="Fichiers">
      <h3 className="lrn-h3">Fichiers</h3>
      <div className="lrn-file-grid">
        {files.map(f => (
          <Card key={f.name} title={f.name} meta={realMeta(f.meta)} media={<span className="lrn-file-icon"><FileText size={20} strokeWidth={1.75} aria-hidden /></span>}
            action={<Button variant="secondary" size="sm" disabled aria-label={`Télécharger ${f.name}`}>Télécharger</Button>} />
        ))}
      </div>
    </section>
  );
}

function ArticleText({ item }: { item: Lesson | Resource }) {
  const parts = "parts" in item ? item.parts : undefined;
  if (!item.excerpt?.length && !parts?.length) return null;
  return (
    <div className="lrn-prose">
      {item.excerpt?.map(p => <p key={p}>{p}</p>)}
      {parts?.map(p => <section key={p.h}><h3 className="lrn-h3">{p.h}</h3><p>{p.t}</p></section>)}
    </div>
  );
}


const PRACTICE=[{question:"Quelle est la première étape d’un échange utile ?",options:["Comprendre le besoin de la personne","Présenter immédiatement tous les tarifs","Proposer une remise"],answer:0},{question:"Après la séance découverte, que préparer ?",options:["Un message identique pour tout le monde","Une prochaine étape concrète liée au besoin","Aucun suivi"],answer:1},{question:"Quel indicateur aide à ajuster tes relances ?",options:["Le nombre de mots du message","Les résultats et retours observés","Le nombre de modèles enregistrés"],answer:1}];
function LearningPractice(){const [answers,setAnswers]=useState<Record<number,number>>({});const [checked,setChecked]=useState(false);return <section className="lrn-practice"><h3>Mettre en pratique</h3><p>Avant ta prochaine séance découverte, note le besoin principal de la personne. Prépare une question ouverte et une prochaine étape réaliste. Après l’échange, relève les points qui ont aidé la personne à décider, puis ajuste ta relance sans changer tout ton processus.</p><h3>Vérifie tes repères</h3>{PRACTICE.map((q,i)=><div key={q.question}><p>{i+1}. {q.question}</p><ChipGroup label={`Question ${i+1}`} options={q.options.map((label,n)=>({value:String(n),label}))} value={answers[i]===undefined?[]:[String(answers[i])]} onValueChange={v=>{setAnswers(a=>{const next={...a};if(v.length)next[i]=Number(v[v.length-1]);else delete next[i];return next;});setChecked(false);}} />{checked&&<p role="status">{answers[i]===q.answer?"Bien vu.":`À retenir : ${q.options[q.answer]}.`}</p>}</div>)}<Button disabled={Object.keys(answers).length!==3} onClick={()=>setChecked(true)}>Vérifier mes réponses</Button></section>}

// ── Lecteur de cours ─────────────────────────────────────────────────────────

function LessonView({ lesson, onOpen, titleRef }: { lesson: Lesson; onOpen: (id: string) => void; titleRef: Ref<HTMLHeadingElement> }) {
  const s = useActivities();
  const p = s.learn;
  const step = STEPS[lesson.step]!;
  const head = (
    <header className="lrn-lesson-head">
      <p className="lrn-kicker">Étape {step.n} · {step.title}</p>
      <h2 className="lrn-title" ref={titleRef} tabIndex={-1}>{lesson.title}</h2>
    </header>
  );
  if (lesson.soon) return <article className="lrn-lesson">{head}<EmptyState icon={false} title="Bientôt disponible" description="Cette séance sera ajoutée à l’étape prochainement." /></article>;
  if (stepLocked(p, lesson.step)) {
    const pre = STEPS[step.prereq!]!;
    const target = pre.lessons.find(id => lessonOpenable(p, id) && !p.done[id]) ?? pre.lessons.find(id => lessonOpenable(p, id));
    return (
      <article className="lrn-lesson">{head}
        <EmptyState icon={<Lock size={22} />} title="Étape verrouillée" description={`Termine d’abord l’étape ${pre.n} · ${pre.title}.`}
          action={target ? <Button variant="secondary" onClick={() => onOpen(target)}>Aller à l’étape {pre.n}</Button> : undefined} />
      </article>
    );
  }
  const done = p.done[lesson.id];
  const cover = lesson.cover ? LESSON_COVERS[lesson.cover] : undefined;
  return (
    <article className="lrn-lesson">
      {head}
      <div className="lrn-badges">
        <Badge size="sm">{formatLabel(lesson.format)}</Badge>
        <Badge size="sm" tone="info">{lesson.theme}</Badge>
        {lesson.format !== "video" && lesson.dur && <Badge size="sm">{lesson.dur}</Badge>}
        {done && <Badge size="sm" tone="success" icon={<Check size={12} />}>Terminée le {dayLabel(done)}</Badge>}
      </div>
      
      
      {lesson.format === "video" && (
        <VideoBlock id={lesson.id} durSec={lesson.durSec} pos={p.pos[lesson.id]} done={!!done} mediaUrl={(lesson as Lesson & WithMedia).mediaUrl ?? "./assets/tc-course-demo-0810.mp4"} trackPosition />
      )}
      {lesson.summary && <p className="lrn-lead">{lesson.summary}</p>}<ArticleText item={lesson} /><LearningPractice key={lesson.id} />
      {lesson.files && lesson.files.length > 0 && <Files files={lesson.files} />}
    </article>
  );
}

function CourseTree({ level, lessonId, onOpen }: { level: Level; lessonId?: string; onOpen: (id: string) => void }) {
  const s = useActivities();
  const p = s.learn;
  const nodes: TreeNode[] = level.steps.map(id => STEPS[id]!).map(st => ({
    id: st.id,
    label: `Étape ${st.n} · ${st.title}${stepLocked(p, st.id) ? " · verrouillée" : stepComplete(p, st.id) ? " · terminée" : ""}`,
    icon: stepLocked(p, st.id) ? <Lock size={14} aria-hidden /> : undefined,
    children: st.lessons.map(id => LESSONS[id]!).map(l => ({
      id: l.id,
      label: `${l.title}${l.soon ? " · bientôt" : p.done[l.id] ? " · terminée" : p.pos[l.id] ? " · en cours" : ""}`,
      icon: p.done[l.id] ? <Check size={14} aria-hidden /> : undefined,
    })),
  }));
  if (!nodes.length) return <p className="lrn-muted">{level.stepCount ? `${level.stepCount} étapes, ouvertes quand le niveau est inclus.` : "Le contenu de ce niveau est en préparation."}</p>;
  return (
    <TreeView key={level.id} aria-label={`Sommaire du ${level.title}`} nodes={nodes} defaultExpandedIds={level.steps} selectedId={lessonId ?? null} current="page"
      onSelect={node => { if (LESSONS[node.id]) onOpen(node.id); }} />
  );
}

function LevelSummary({ level, onExit }: { level: Level; onExit?: () => void }) {
  const s = useActivities();
  const ids = levelCountable(level), done = ids.filter(id => s.learn.done[id]).length;
  return (
    <div className="lrn-side-head">
      <img className="lrn-side-cover" src={levelCover(level)} alt="" />
      <div className="lrn-side-title">
        <div className="lrn-level-identity">{onExit && <Button variant="ghost" shape="icon" size="sm" aria-label="Sortir de la formation" onClick={onExit}><LogOut size={17}/></Button>}<p className="lrn-kicker">{level.title}</p></div>
        <p className="lrn-side-sub">{level.subtitle}</p>
      </div>
      {level.access === "included" && ids.length > 0 && <Progress value={done} max={ids.length} label={`${done} séance${done > 1 ? "s" : ""} sur ${ids.length}`} showValue />}
    </div>
  );
}

function CourseReader({ level, lessonId, open, exit, navigate, mobile }: { level: Level; lessonId?: string; open: (id: string) => void; exit: () => void; navigate: PageProps["navigate"]; mobile: boolean }) {
  const s = useActivities();
  const p = s.learn;
  const [sheet, setSheet] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const lesson = lessonId ? LESSONS[lessonId] : undefined;

  // A new lesson starts at the top of the page and gives its title the focus.
  useEffect(() => {
    if (!lessonId) return;
    document.getElementById("tc-main")?.scrollTo({ top: 0 });
    titleRef.current?.focus({ preventScroll: true });
  }, [lessonId]);

  // Opening a lesson makes it the current one (learn.last) with its saved position, never marks it done.
  // Once per id; a « bientôt », locked or not included lesson is not forced. Reads the store at the time of the effect.
  const progressRef = useRef(p);
  progressRef.current = p;
  const followedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!lessonId || followedRef.current === lessonId) return;
    followedRef.current = lessonId;
    const cur = progressRef.current;
    if (level.access !== "included" || !lessonOpenable(cur, lessonId) || cur.last === lessonId) return;
    setLessonPosition(lessonId, cur.pos[lessonId] ?? 0);
  }, [lessonId]);

  const openFromTree = (id: string) => { setSheet(false); open(id); };
  const prev = lesson ? neighbour(p, level, lesson.id, -1) : undefined;
  const next = lesson ? neighbour(p, level, lesson.id, 1) : undefined;
  const canMark = !!lesson && lessonOpenable(p, lesson.id);
  const done = !!lesson && !!p.done[lesson.id];
  const backButton = <Button variant="ghost" leading={<ArrowLeft size={16} aria-hidden />} onClick={exit}>Sortir</Button>;

  const body = level.access === "not_included"
    ? <EmptyState icon={<Lock size={22} />} title={`${level.title} non inclus`} description={`${level.subtitle} : ce niveau ne fait pas partie de ton accompagnement actuel.`}
        action={<Button variant="secondary" onClick={() => navigate("shop")}>Voir les offres</Button>} />
    : level.access === "soon"
      ? <EmptyState icon={false} title={`${level.title} bientôt disponible`} description={level.subtitle} />
      : !lesson
        ? <EmptyState icon={false} title="Aucune séance ouverte pour l’instant" description="Les séances de ce niveau apparaîtront ici dès qu’elles seront publiées." />
        : <LessonView key={lesson.id} lesson={lesson} onOpen={open} titleRef={titleRef} />;

  const finish = () => { if(!lesson||!canMark)return; markLesson(lesson.id,true); const after={...p,done:{...p.done,[lesson.id]:"2026-10-08"}}; const following=neighbour(after,level,lesson.id,1);if(following&&!following.locked)open(following.id); };
  const actions = lesson && <nav className="lrn-actions" data-mobile={mobile||undefined} aria-label="Navigation entre les séances">
    <Button variant="secondary" leading={<ChevronLeft size={16}/>} disabled={!prev||prev.locked} onClick={()=>prev&&open(prev.id)}>Précédent</Button>
    <Button disabled={!canMark} leading={<Check size={16}/>} onClick={finish}>Terminé</Button>
  </nav>;
  const lockedNote = next?.locked && !mobile ? <p className="lrn-muted lrn-next-note">La suite s’ouvre quand l’étape en cours est terminée.</p> : null;

  if (mobile) {
    return (
      <div className="lrn-reader" data-mobile>
        <div className="lrn-reader-controls">
          <div className="lrn-level-identity"><Button variant="ghost" shape="icon" size="sm" aria-label="Sortir de la formation" onClick={exit}><LogOut size={17}/></Button><span>{level.title}</span></div>
          <Button variant="secondary" size="sm" leading={<ListTree size={16} aria-hidden />} onClick={() => setSheet(true)}>Sommaire</Button>
        </div>
        <div className="lrn-reader-main" data-has-actions={actions ? true : undefined}>{body}</div>
        {actions}
        <BottomSheet open={sheet} onOpenChange={setSheet} title={`Sommaire · ${level.title}`} description={level.subtitle} showClose={false} detents={[.6, .92]}>
          <div className="lrn-sheet">
            <LevelSummary level={level} />
            <CourseTree level={level} lessonId={lessonId} onOpen={openFromTree} />
          </div>
        </BottomSheet>
      </div>
    );
  }
  return (
    <div className="lrn-reader">
      <aside className="lrn-side" aria-label={`Cours ${level.title}`}>
        <LevelSummary level={level} onExit={exit} />
        <CourseTree level={level} lessonId={lessonId} onOpen={open} />
      </aside>
      <div className="lrn-reader-main">
        {body}
        {actions}
        {lockedNote}
      </div>
    </div>
  );
}


function ResourceTool({resource}:{resource:Resource}) {
  const [clients,setClients]=useState([{name:"Camille",stage:"Séance découverte",next:"Envoyer le récapitulatif"},{name:"Julien",stage:"Relance",next:"Proposer un créneau"}]);
  const [rate,setRate]=useState("65"),[sessions,setSessions]=useState("20"),[cost,setCost]=useState("350");
  if(resource.id==="t2")return <section className="lrn-tool-mock"><div className="lrn-tool-fields"><label>Tarif par séance (€)<Input type="number" min="0" value={rate} onChange={e=>setRate(e.target.value)}/></label><label>Séances par mois<Input type="number" min="0" value={sessions} onChange={e=>setSessions(e.target.value)}/></label><label>Charges mensuelles (€)<Input type="number" min="0" value={cost} onChange={e=>setCost(e.target.value)}/></label></div><div className="lrn-tool-result"><span>Résultat mensuel estimé</span><strong>{(Number(rate)*Number(sessions)-Number(cost)).toLocaleString("fr-FR")} €</strong></div></section>;
  return <section className="lrn-tool-mock"><table><thead><tr><th>Client</th><th>Étape</th><th>Prochaine action</th></tr></thead><tbody>{clients.map((c,i)=><tr key={i}><td><Input aria-label={`Client ${i+1}`} value={c.name} onChange={e=>setClients(v=>v.map((x,n)=>n===i?{...x,name:e.target.value}:x))}/></td><td>{c.stage}</td><td><Input aria-label={`Prochaine action de ${c.name}`} value={c.next} onChange={e=>setClients(v=>v.map((x,n)=>n===i?{...x,next:e.target.value}:x))}/></td></tr>)}</tbody></table><Button variant="secondary" onClick={()=>setClients(v=>[...v,{name:"",stage:"À contacter",next:""}])}>Ajouter un client</Button></section>;
}

// ── Ressource (page dédiée, sans sidebar de cours) ───────────────────────────

function ResourceView({ resource, exit }: { resource: Resource; exit: () => void }) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { document.getElementById("tc-main")?.scrollTo({ top: 0 }); titleRef.current?.focus({ preventScroll: true }); }, [resource.id]);
  return (
    <div className="lrn-page lrn-resource">
      <div className="lrn-reader-top"><Button variant="ghost" leading={<ArrowLeft size={16} aria-hidden />} onClick={exit}>Sortir</Button></div>
      <article className="lrn-lesson">
        <header className="lrn-lesson-head">
          <p className="lrn-kicker">Bibliothèque · {COLLECTION_LABEL[resource.coll]}</p>
          <h2 className="lrn-title" ref={titleRef} tabIndex={-1}>{resource.title}</h2>
        </header>
        <div className="lrn-badges">
          <Badge size="sm">{formatLabel(resource.format)}</Badge>
          <Badge size="sm" tone="info">{resource.theme}</Badge>
          {realMeta(resource.meta) && <Badge size="sm">{realMeta(resource.meta)}</Badge>}
        </div>
        {resource.summary && <p className="lrn-lead">{resource.summary}</p>}
        {resource.format === "video" && <VideoBlock id={resource.id} durSec={resource.durSec} mediaUrl={(resource as Resource & WithMedia).mediaUrl} trackPosition={false} />}
        {resource.replayUnavailable && <Notice icon={<PlayCircle size={18} aria-hidden />}>Replay indisponible</Notice>}
        <ArticleText item={resource} />
        {resource.files && resource.files.length > 0 && <Files files={resource.files} />}
        {resource.ext && <ResourceTool resource={resource} />}
      </article>
    </div>
  );
}

// ── Accueil Apprendre ────────────────────────────────────────────────────────

function ResumeCard({ onOpen }: { onOpen: (level: string, lesson: string) => void }) {
  const s = useActivities();
  const p = s.learn;
  const lastLevel = levelOfLesson(p.last) ?? LEVELS.find(lv => lv.access === "included");
  if (!lastLevel) return null;
  const target = lessonOpenable(p, p.last) && !p.done[p.last] ? p.last : entryLesson(p, lastLevel);
  const lesson = target ? LESSONS[target] : undefined;
  if (!lesson) return null;
  const step = STEPS[lesson.step]!;
  const pos = p.pos[lesson.id];
  const ids = levelCountable(lastLevel), done = ids.filter(id => p.done[id]).length;
  const started = pos !== undefined && pos > 0;
  return (
    <CardSurface className="lrn-resume" padding="none" radius="lg" onClick={() => onOpen(lastLevel.id, lesson.id)}>
      <img className="lrn-resume-cover" src={levelCover(lastLevel)} alt="" />
      <div className="lrn-resume-body">
        <p className="lrn-kicker">{lastLevel.title} · Étape {step.n}</p>
        <h2 className="lrn-resume-title">{lesson.title}</h2>
        <p className="lrn-resume-meta">{[formatLabel(lesson.format), lesson.format === "video" ? undefined : lesson.dur].filter(Boolean).join(" · ")}</p>
        {started && lesson.durSec !== undefined
          ? <Progress value={Math.min(pos,lesson.format==="video"?50:lesson.durSec)} max={lesson.format==="video"?50:lesson.durSec} label={`Reprise à ${fmtClock(Math.min(pos,lesson.format==="video"?50:lesson.durSec))}`} />
          : <Progress value={done} max={Math.max(ids.length, 1)} label={`${lastLevel.title} : ${done} séance${done > 1 ? "s" : ""} sur ${ids.length}`} />}
        <Button trailing={<ChevronRight size={16} aria-hidden />} onClick={() => onOpen(lastLevel.id, lesson.id)}>{started ? "Reprendre la séance" : "Ouvrir la séance"}</Button>
      </div>
    </CardSurface>
  );
}

function LevelCards({ onOpen }: { onOpen: (level: Level) => void }) {
  const s = useActivities();
  const currentLevel = levelOfLesson(s.learn.last)?.id;
  return (
    <section className="lrn-section" aria-labelledby="lrn-levels-h">
      <h2 className="lrn-h2" id="lrn-levels-h">Mon parcours</h2>
      <div className="lrn-levels">
        {LEVELS.map(lv => {
          const ids = levelCountable(lv), done = ids.filter(id => s.learn.done[id]).length;
          const access = ACCESS[lv.access];
          return (
            <CardSurface key={lv.id} asChild padding="none" radius="lg">
              <ButtonCore variant="ghost" accent={false} morph={false} className="lrn-level" data-current={lv.id === currentLevel || undefined} data-access={lv.access}
                aria-label={`${lv.title}, ${lv.subtitle}, ${access.label}${lv.access === "included" ? `, ${done} séances sur ${ids.length}` : ""}. Ouvrir le cours`}
                onClick={() => lv.access==="not_included" ? (window.location.hash="/shop?offer=f1") : lv.access==="soon" ? (window.location.hash="/shop") : onOpen(lv)}>
                <img className="lrn-level-cover" src={levelCover(lv)} alt="" loading="lazy" />{lv.access!=="included"&&<span className="lrn-level-lock"><Lock size={30} strokeWidth={1.6}/></span>}
                <span className="lrn-level-body">
                  <span className="lrn-level-head">
                    <b>{lv.title}</b>
                    {lv.access==="included"&&<Badge size="sm" tone={access.tone}>{lv.id === currentLevel?"En cours":"Inclus"}</Badge>}
                  </span>
                  <span className="lrn-level-sub">{lv.subtitle}</span>
                  {lv.access === "included"
                    ? <Progress value={done} max={Math.max(ids.length, 1)} label={`${done} séance${done > 1 ? "s" : ""} sur ${ids.length}`} />
                    : <span className="lrn-level-sub">{lv.stepCount ? `${lv.stepCount} étapes` : "Contenu en préparation"}</span>}
                </span>
              </ButtonCore>
            </CardSurface>
          );
        })}
      </div>
    </section>
  );
}

/** Library preferences and home scroll, kept at module level: they survive the unmount of the home (reader, resource)
 *  and a remount of LearnExperience. A Foundry adapter may back this with the member preferences. */
const libraryMemory: { colls: string[]; themes: string[]; homeScroll?: number } = { colls: [], themes: [] };

function useLibraryFilter(key: "colls" | "themes") {
  const [value, setValue] = useState<string[]>(() => libraryMemory[key]);
  const set = useCallback((next: string[]) => { libraryMemory[key] = next; setValue(next); }, [key]);
  return [value, set] as const;
}

function Library({ onOpen }: { onOpen: (r: Resource) => void }) {
  const [expandedFilter, setExpandedFilter] = useState<"type" | "subject" | null>(null);
  const [colls, setColls] = useLibraryFilter("colls");
  const [themes, setThemes] = useLibraryFilter("themes");
  const themeOptions = useMemo(() => Array.from(new Set(RESOURCES.map(r => r.theme))).map(t => ({ value: t, label: t })), []);
  const resourceFormat=(r:Resource)=>r.format==="video"?"video":r.ext?"spreadsheet":r.format==="file"?"document":"article";
  const collOptions = [{value:"document",label:"Documents"},{value:"spreadsheet",label:"Tableurs et outils"},{value:"video",label:"Vidéos"},{value:"article",label:"Articles"}];
  const items = RESOURCES.filter(r => (!colls.length || colls.includes(resourceFormat(r))) && (!themes.length || themes.includes(r.theme)));
  const filtered = colls.length > 0 || themes.length > 0;
  const clear = () => { setColls([]); setThemes([]); };
  return (
    <section className="lrn-section" aria-labelledby="lrn-lib-h">
      <div className="lrn-section-head">
        <h2 className="lrn-h2 lrn-library-title" id="lrn-lib-h">Bibliothèque <span aria-live="polite">({items.length} contenu{items.length > 1 ? "s" : ""})</span></h2>
        {filtered && <Button variant="ghost" size="sm" onClick={clear}>Effacer les filtres</Button>}
      </div>
      <div className="lrn-filters">
        <Button variant="secondary" size="sm" aria-expanded={expandedFilter === "type"} onClick={() => setExpandedFilter(expandedFilter === "type" ? null : "type")}>Type{colls.length ? ` · ${colls.length}` : ""}<ChevronRight size={14} className={expandedFilter === "type" ? "lrn-filter-chevron-open" : ""}/></Button>
        {expandedFilter === "type" && <div className="lrn-filter-options"><ChipGroup label="Type" options={collOptions} value={colls} onValueChange={setColls} /></div>}
        <Button variant="secondary" size="sm" aria-expanded={expandedFilter === "subject"} onClick={() => setExpandedFilter(expandedFilter === "subject" ? null : "subject")}>Sujet{themes.length ? ` · ${themes.length}` : ""}<ChevronRight size={14} className={expandedFilter === "subject" ? "lrn-filter-chevron-open" : ""}/></Button>
        {expandedFilter === "subject" && <div className="lrn-filter-options"><ChipGroup label="Sujet" options={themeOptions} value={themes} onValueChange={setThemes} /></div>}
      </div>
      {items.length === 0
        ? <EmptyState icon={false} title="Aucun contenu pour ces filtres" action={<Button variant="secondary" onClick={clear}>Effacer les filtres</Button>} />
        : (
          <div className="lrn-lib-grid">
            {items.map(r => (
              <CardSurface key={r.id} asChild padding="md"><ButtonCore variant="ghost" accent={false} morph={false} className="lrn-resource-card" onClick={()=>onOpen(r)}><FileText size={24}/><span><strong>{r.title}</strong><small>{[r.theme,realMeta(r.meta)].filter(Boolean).join(" · ")}</small></span><ChevronRight size={18}/></ButtonCore></CardSurface>
            ))}
          </div>
        )}
    </section>
  );
}

// ── Point d’entrée ───────────────────────────────────────────────────────────

export function LearnExperience({ navigate, onReaderChange }: LearnExperienceProps) {
  const s = useActivities();
  const mobile = useMediaQuery(MOBILE_QUERY);
  const [loc, go] = useLearnLocation();
  const level = loc.level ? LEVELS.find(lv => lv.id === loc.level) : undefined;
  const resource = loc.resource ? RESOURCES.find(r => r.id === loc.resource) : undefined;
  const readerActive = !!level;

  // A level without a lesson in its address opens on its entry lesson; the address is completed in place (refresh-stable).
  const entry = level && !loc.lesson && level.access === "included" ? entryLesson(s.learn, level) : undefined;
  useEffect(() => { if (level && entry) go({ level: level.id, lesson: entry }, "replace"); }, [level, entry, go]);

  const readerRef = useRef(onReaderChange);
  readerRef.current = onReaderChange;
  useEffect(() => { readerRef.current?.(readerActive); }, [readerActive]);
  useEffect(() => () => readerRef.current?.(false), []);

  // Back on the home after a reading: the page returns where it was left (filters are kept by the Library itself).
  const atHome = !resource && !level;
  useEffect(() => {
    if (!atHome || libraryMemory.homeScroll === undefined) return;
    document.getElementById("tc-main")?.scrollTo({ top: libraryMemory.homeScroll });
    libraryMemory.homeScroll = undefined;
  }, [atHome]);

  const home = () => go({});
  const leaveHome = (next: LearnLocation) => { libraryMemory.homeScroll = document.getElementById("tc-main")?.scrollTop; go(next); };
  if (resource) return <ResourceView resource={resource} exit={home} />;
  if (level) {
    return <CourseReader level={level} lessonId={loc.lesson ?? entry} mobile={mobile} navigate={navigate} exit={home}
      open={id => go({ level: level.id, lesson: id })} />;
  }
  return (
    <div className="lrn-page lrn-home">
      <ResumeCard onOpen={(lv, lesson) => leaveHome({ level: lv, lesson })} />
      <LevelCards onOpen={lv => leaveHome({ level: lv.id, lesson: lv.access === "included" ? entryLesson(s.learn, lv) : undefined })} />
      <Library onOpen={r => leaveHome({ resource: r.id })} />
    </div>
  );
}

export default LearnExperience;
